import React from 'react';
import {
  LayoutDashboard,
  UploadCloud,
  Table,
  FileSpreadsheet,
  History,
  ShieldCheck,
  Zap,
  CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useReconciliation } from '../../context/ReconciliationContext';

export type TabType =
  | 'dashboard'
  | 'upload'
  | 'master_table'
  | 'reports'
  | 'history'
  | 'admin';

interface SidebarProps {
  currentTab: TabType;
  onTabChange: (tab: TabType) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentTab, onTabChange }) => {
  const { isAdmin } = useAuth();
  const { isReconciled, summary, loadDemoData, isProcessing } = useReconciliation();

  const navItems: { id: TabType; label: string; icon: React.FC<{ className?: string }>; badge?: string; adminOnly?: boolean }[] = [
    {
      id: 'dashboard',
      label: 'Financial Dashboard',
      icon: LayoutDashboard,
    },
    {
      id: 'upload',
      label: 'Upload & Mapping',
      icon: UploadCloud,
    },
    {
      id: 'master_table',
      label: 'Master Reconciliation',
      icon: Table,
      badge: summary ? `${summary.totalGroups}` : undefined,
    },
    {
      id: 'reports',
      label: 'Reports & Exports',
      icon: FileSpreadsheet,
      badge: '8 Reports',
    },
    {
      id: 'history',
      label: 'Audit & Run History',
      icon: History,
    },
    {
      id: 'admin',
      label: 'Admin & Rules',
      icon: ShieldCheck,
      adminOnly: true,
    },
  ];

  return (
    <aside className="w-64 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col justify-between py-5 px-3 min-h-[calc(100vh-4rem)]">
      <div className="space-y-6">
        {/* Navigation list */}
        <nav className="space-y-1">
          {navItems.map((item) => {
            if (item.adminOnly && !isAdmin) return null;
            const Icon = item.icon;
            const isActive = currentTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300 shadow-sm border border-brand-200/60 dark:border-brand-800/60 font-semibold'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/70 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-brand-600 dark:text-brand-400' : 'text-slate-400'}`} />
                  <span>{item.label}</span>
                </div>
                {item.badge && (
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full font-mono font-medium ${
                      isActive
                        ? 'bg-brand-200/80 text-brand-900 dark:bg-brand-900 dark:text-brand-200'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Quick Demo Loader Banner */}
        <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-50 to-brand-50/40 dark:from-slate-800/60 dark:to-brand-950/20 border border-slate-200/80 dark:border-slate-800">
          <div className="flex items-center gap-2 text-xs font-semibold text-brand-800 dark:text-brand-300 mb-1.5">
            <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
            <span>Instant Demo Dataset</span>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed mb-3">
            Load pre-populated Scheme App payments & multi-bill ERP vouchers to test all 6 business rules.
          </p>
          <button
            onClick={loadDemoData}
            disabled={isProcessing}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:border-brand-500 text-slate-800 dark:text-slate-200 hover:text-brand-600 dark:hover:text-brand-400 shadow-sm transition disabled:opacity-50"
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            {isProcessing ? 'Processing Engine...' : 'Reload Demo Data'}
          </button>
        </div>
      </div>

      {/* Footer System Status */}
      <div className="pt-4 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 space-y-1">
        <div className="flex items-center justify-between">
          <span>Engine Status</span>
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Online (D1 Ready)
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span>Aggregator</span>
          <span className="font-mono text-slate-700 dark:text-slate-300">1-to-Many Active</span>
        </div>
      </div>
    </aside>
  );
};
